// ============================================================================
// battlefield.js — the arena host. Instead of one fixed fort, it REBUILDS the
// field per level from render/arenas.js: framing terrain, landmarks, instanced
// scatter, set-piece lights, and the four capturable points (re-skinned + placed
// per arena). It still exposes the gameplay contract every other system reads:
// world.bastions (id 0-3, team, x/z, bannerMat, light) + world.gates, plus the
// N/S anchors (enemy citadel north, player muster south) that spawns/warlords/
// generals use. buildArena() clears the previous scene and raises a new place.
// ============================================================================

import * as THREE from 'three';
import { TEAM_PLAYER, TEAM_MAGMAR, TEAM_ACCENT } from '../core/constants.js';
import { ARENA_BUILDERS, arenaKeyFor, PAL } from '../render/arenas.js';
import { LANDMARK, mat } from '../render/props.js';

// field anchors (metres). Enemy citadel north (−Z), player muster south (+Z).
export const CITADEL = { x: 0, z: -152 };
export const WARCAMP = { x: 0, z: 150 };
export const PLAYER_START = { x: 0, z: 118, heading: Math.PI };

const _loader = new THREE.TextureLoader();
function tiled(url, srgb) {
  const t = _loader.load(url); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.anisotropy = 8;
  if (srgb) t.colorSpace = THREE.SRGBColorSpace; return t;
}
function tileBoxUVs(geo, w, h, d, tile) {
  const uv = geo.attributes.uv;
  const dims = [[d, h], [d, h], [w, d], [w, d], [w, h], [w, h]];
  for (let f = 0; f < 6; f++) { const su = dims[f][0] / tile, sv = dims[f][1] / tile; for (let i = 0; i < 4; i++) { const k = f * 4 + i; uv.setXY(k, uv.getX(k) * su, uv.getY(k) * sv); } }
  uv.needsUpdate = true;
}

const CAP_NAME = { camp: 'War-Camp', tower: 'Watchtower', shrine: 'Old Shrine', barn: 'Grange', windmill: 'Mill', siege: 'Siege-Works', brazier: 'Pyre', pier: 'Pier-Fort' };

export class Battlefield {
  constructor(renderer, world) {
    this.world = world;
    this.group = new THREE.Group();      // ALL arena geometry lives here; cleared each buildArena
    renderer.add(this.group);

    // road / capture-platform stone (tintable via setStructTint), banner textures
    const wallTex = tiled('art/wall.png', true);
    this._rock = new THREE.MeshLambertMaterial({ color: 0xcfc7bd, map: wallTex, emissive: 0xff5210, emissiveIntensity: 0.22, emissiveMap: tiled('art/wall.png', false) });
    this._dark = new THREE.MeshLambertMaterial({ color: 0x6b6058, map: wallTex });
    this._road = new THREE.MeshLambertMaterial({ color: 0xccc3b8, map: tiled('art/road.png', true), emissive: 0x3a1608, emissiveIntensity: 0.12, emissiveMap: tiled('art/road.png', false) });
    this._bannerTex = { [TEAM_PLAYER]: tiled('art/banner_host.png', true), [TEAM_MAGMAR]: tiled('art/banner_magmar.png', true) };

    this._anims = [];
    this._t = 0;
    this.buildArena(world, world.level || { biome: 'ashlands' });   // a default scene for the menu
  }

  // ---- (re)build the whole field for a level ------------------------------
  buildArena(world, level) {
    this._dispose();
    world.bastions.length = 0;
    world.gates = [];
    world.colliders.clear();
    this._anims = [];
    this._capId = 0;

    const biome = (level && level.biome) || 'ashlands';
    const key = arenaKeyFor(level || {});
    const palette = PAL[biome] || PAL.ashlands;

    const ctx = {
      group: this.group, world, biome, arena: key, palette,
      broken: false,
      light: (color, intensity, dist, x, y, z) => this._light(color, intensity, dist, x, y, z),
      anim: (fn) => this._anims.push(fn),
      fire: (x, z, scale) => this._fire(x, z, scale),
      stoneMat: (hex) => mat(hex),
      banner: (x, z, team, scale) => this._banner(x, z, team, scale),
      // environment collision — landmarks/arenas register their footprint here
      solid: { circle: (x, z, r) => world.colliders.circle(x, z, r), box: (x, z, hx, hz, a) => world.colliders.box(x, z, hx, hz, a || 0) },
      captures: (list) => { for (const [x, z, skin] of list) this._addCapture(ctx, x, z, skin); },
    };
    (ARENA_BUILDERS[key] || ARENA_BUILDERS.ash_barrens)(ctx);

    // safety: if an arena somehow registered fewer than 4 capture points, top up
    while (world.bastions.length < 4) { const i = world.bastions.length; this._addCapture(ctx, (i < 2 ? -1 : 1) * 96, i % 2 ? 56 : -46, 'camp'); }
  }

  _dispose() {
    const g = this.group;
    for (let i = g.children.length - 1; i >= 0; i--) {
      const c = g.children[i];
      // recurse so nested landmark geometry (e.g. the windmill's sail Group) is
      // freed too — only geometry is unique per build; materials are shared caches.
      c.traverse((o) => { if (o.geometry) o.geometry.dispose(); });
      g.remove(c);
    }
  }

  _light(color, intensity, dist, x, y, z) {
    const L = new THREE.PointLight(color, intensity, dist, 2);
    L.position.set(x, y, z); this.group.add(L); return L;
  }

  // a flickering fire: emissive flame cones + a warm point light + rising embers
  _fire(x, z, scale = 1) {
    const s = scale;
    const flame = new THREE.Mesh(new THREE.ConeGeometry(0.7 * s, 2.2 * s, 6),
      new THREE.MeshBasicMaterial({ color: 0xff8a2a }));
    flame.position.set(x, 1.1 * s, z); this.group.add(flame);
    const logs = new THREE.Mesh(new THREE.CylinderGeometry(0.7 * s, 0.7 * s, 0.5, 6), mat(0x2a2018));
    logs.position.set(x, 0.25, z); this.group.add(logs);
    const L = this._light(0xff6a1a, 2.0 * s, 34 * s, x, 2 * s, z);
    this._anims.push((dt, t) => {
      const f = 0.85 + Math.sin(t * 12 + x) * 0.12 + Math.sin(t * 7 + z) * 0.08;
      flame.scale.set(1, f, 1); flame.rotation.y = t * 2;
      L.intensity = (1.8 + f * 0.8) * s;
    });
  }

  // build a capturable point (platform + team banner + light + skin decoration)
  _addCapture(ctx, x, z, skin) {
    const id = this._capId++;
    // platform (tintable stone)
    const geo = new THREE.BoxGeometry(18, 2, 18); tileBoxUVs(geo, 18, 2, 18, 4);
    const plat = new THREE.Mesh(geo, this._rock); plat.position.set(x, 1, z); this.group.add(plat);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) { const pg = new THREE.BoxGeometry(2.4, 6, 2.4); const pm = new THREE.Mesh(pg, this._rock); pm.position.set(x + sx * 7, 3, z + sz * 7); this.group.add(pm); }

    // skin-specific topper (uses the arena palette). solid:false — a capture point
    // must stay walkable so units can contest it and warlords aren't trapped in it.
    if (skin === 'tower') LANDMARK.watchtower(ctx, x, z, { h: 11, solid: false });
    else if (skin === 'shrine') LANDMARK.shrine(ctx, x, z, { solid: false });
    else if (skin === 'barn' || skin === 'windmill') LANDMARK.barn(ctx, x, z, { solid: false });
    else if (skin === 'siege') LANDMARK.siegeTower(ctx, x, z, { solid: false });
    else if (skin === 'pier') LANDMARK.watchtower(ctx, x, z, { h: 8, solid: false });
    else if (skin === 'brazier') this._fire(x, z, 1.6);
    else { LANDMARK.tent(ctx, x - 4, z + 3, { rot: 0.4 }); LANDMARK.tent(ctx, x + 4, z - 2, { rot: -0.5 }); }   // 'camp'

    const b = this._banner(x, z + (skin === 'tower' || skin === 'siege' ? 6 : 4), TEAM_MAGMAR, 1.7);
    const light = this._light(TEAM_ACCENT[TEAM_MAGMAR], 1.0, 44, x, 9, z);

    this.world.bastions.push({
      id, name: CAP_NAME[skin] || 'Hold', x, z, team: TEAM_MAGMAR, radius: 12,
      captainSlot: -1, bannerMat: b.mat, light, respawnT: 0, contestT: 0,
    });
  }

  _banner(x, z, team, scale) {
    const tex = this._bannerTex[team];
    const bmat = new THREE.MeshLambertMaterial({ map: tex, emissive: TEAM_ACCENT[team], emissiveIntensity: 0.3, emissiveMap: tex, side: THREE.DoubleSide });
    const pole = new THREE.Mesh(new THREE.BoxGeometry(0.3, 6 * scale, 0.3), this._dark); pole.position.set(x, 3 * scale, z); this.group.add(pole);
    const cgeo = new THREE.PlaneGeometry(2.2 * scale, 3.4 * scale); const cuv = cgeo.attributes.uv;
    for (let i = 0; i < cuv.count; i++) cuv.setXY(i, 0.2 + cuv.getX(i) * 0.6, 0.04 + cuv.getY(i) * 0.93);
    cuv.needsUpdate = true;
    const cloth = new THREE.Mesh(cgeo, bmat); cloth.position.set(x, 4.2 * scale, z + 0.2); this.group.add(cloth);
    return { mat: bmat, cloth, pole };
  }

  // ---- gameplay hooks kept from the old fixed field -----------------------
  recolorBastion(bastion) {
    const tex = this._bannerTex[bastion.team];
    bastion.bannerMat.map = tex; bastion.bannerMat.emissiveMap = tex;
    bastion.bannerMat.emissive.setHex(TEAM_ACCENT[bastion.team]);
    bastion.bannerMat.needsUpdate = true;
    bastion.light.color.setHex(TEAM_ACCENT[bastion.team]);
  }
  breakGate(id) {
    const g = this.world.gates && this.world.gates[id];
    if (!g || g.broken) return false;
    g.broken = true; return true;
  }
  resetGates() { for (const g of (this.world.gates || [])) g.broken = false; }

  // tint the road / capture-platform stone for a biome (arenas theme their own landmarks)
  setStructTint(rockHex, emisInt) {
    this._rock.color.setHex(rockHex); this._rock.emissiveIntensity = emisInt;
    this._road.color.setHex(rockHex); this._road.emissiveIntensity = Math.min(0.12, emisInt);
    this._dark.color.setHex(rockHex).multiplyScalar(0.62);
  }

  update(dt, t) {
    this._t = t;
    for (let i = 0; i < this._anims.length; i++) this._anims[i](dt, t);
  }
}
